import React, { useState, useEffect } from 'react';
import { Card, Table, Alert, Badge } from 'react-bootstrap';
import moment from 'moment';
import Pagination from '../common/Pagination';
import hdBonusService from '../../services/hd.bonus.service';
import { hideLoading, showLoading } from '../../lib/uiService';
import { toast } from 'react-toastify';
import { useAuth } from '../../providers/AuthProvider';
import { UserRole } from '../../lib/types';
import HDBonusTable from './HDBonusTable';

interface BonusHistoryTableProps {
  refreshTrigger?: number; // Incremented when parent needs to trigger a refresh
  onSubmissionCheck?: (hasSubmitted: boolean) => void; // Callback to inform parent about existing submissions
}

const BonusHistoryTable: React.FC<BonusHistoryTableProps> = ({ refreshTrigger = 0, onSubmissionCheck }) => {
  const [bonusHistory, setBonusHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  // Use auth hook directly instead of receiving as props
  const { id, role, isCheckingLoggedState } = useAuth();
  const isAdmin = role && role.includes(UserRole.Admin);

  // Fetch bonus history
  useEffect(() => {
    // Don't fetch if auth state is still loading
    if (isCheckingLoggedState) return;

    const fetchBonusHistory = async () => {
      try {
        setLoading(true);
        showLoading();

        const { data } = await hdBonusService.GetHDBonusHistory({
          page: currentPage,
          limit: itemsPerPage,
          all: isAdmin,
        });

        setBonusHistory(data.items);
        setTotalPages(data.total);

        // Check if user has submitted for current month
        if (onSubmissionCheck && id) {
          const currentMonth = moment().format('YYYY-MM');
          const hasSubmitted = data.items.some(
            (item) => item.userId === id && moment(`${item.year}-${item.month}`).format('YYYY-MM') === currentMonth,
          );

          onSubmissionCheck(hasSubmitted);
        }

        hideLoading();
      } catch (error) {
        hideLoading();
        toast.error('Error loading bonus history');
        console.error('Error fetching bonus history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBonusHistory();
  }, [currentPage, isAdmin, id, refreshTrigger, onSubmissionCheck, itemsPerPage, isCheckingLoggedState]);

  // If still checking auth state, show nothing
  if (isCheckingLoggedState) return null;

  return (
    <Card className="mb-4">
      <Card.Header as="h5">Bonus History</Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center py-4">Loading...</div>
        ) : bonusHistory.length === 0 ? (
          <Alert variant="info">No bonus history found.</Alert>
        ) : (
          <>
            <HDBonusTable isAdmin={isAdmin} bonusHistory={bonusHistory} />

            {totalPages > 0 && <Pagination total={totalPages} currentPage={currentPage} setPage={setCurrentPage} />}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default BonusHistoryTable;
